<script setup lang="ts">
    import { useVuelidate } from '@vuelidate/core';
    import { createI18nMessage } from '@vuelidate/validators';
    import { customValidators } from '@/i18n/utils/i18n-validators';
    import { ApiClientError } from '@shopware/api-client';
    import type { ApiError } from '@shopware/api-client';
    import type { Schemas } from '#shopware';

    const { address = undefined } = defineProps<{
        address?: Schemas['CustomerAddress'];
    }>();

    const emits = defineEmits<{
        (e: 'success' | 'close'): void;
    }>();

    const { required, minLength, maxLength } = customValidators();

    const { createCustomerAddress, updateCustomerAddress, errorMessageBuilder } = useAddress();

    const { lang } = useLanguage();
    const { t } = useI18n();

    const { getSalutations } = useSalutations();
    const { hfpPushError } = useNotificationHelpers();

    const formData = reactive({
        countryId: address?.countryId ?? '',
        countryStateId: address?.countryStateId ?? '',
        salutationId: address?.salutationId ?? '',
        firstName: address?.firstName ?? '',
        lastName: address?.lastName ?? '',
        zipcode: address?.zipcode ?? '',
        city: address?.city ?? '',
        street: address?.street ?? '',
        id: address?.id ?? '',
        additionalAddressLine1: address?.additionalAddressLine1 ?? ''
    });

    const invokeSave = async (): Promise<void> => {
        try {
            const saveAddress = formData.id ? updateCustomerAddress : createCustomerAddress;

            $v.value.$touch();
            const valid = await $v.value.$validate();
            if (valid) {
                await saveAddress(formData as Schemas['CustomerAddress']);
                emits('success');
            }
        } catch (errors) {
            if (errors instanceof ApiClientError) {
                errors.details.errors.forEach((element: ApiError) => {
                    hfpPushError(errorMessageBuilder(element) || t('messages.error'));
                });
            }
        }
    };

    const firstNameInputElement = ref();
    useFocus(firstNameInputElement, { initialValue: true });

    const withI18nMessage = createI18nMessage({
        // @ts-expect-error - locale specifically wants "de" and "en" as keys. lang is typed as string
        t: (key, params) => t(key, params, { locale: lang.value }),
        messagePath: ({ $validator }) => `validations.${$validator}`
    });

    // i18n rules
    const requiredI18n = withI18nMessage(required);
    const minLengthI18n = withI18nMessage(minLength, { withArguments: true });
    const maxLengthI18n = withI18nMessage(maxLength, { withArguments: true });

    const rules = computed(() => ({
        salutationId: {
            required: requiredI18n
        },
        firstName: {
            required: requiredI18n,
            minLength: minLengthI18n(3)
        },
        lastName: {
            required: requiredI18n,
            minLength: minLengthI18n(3)
        },
        zipcode: {
            required: requiredI18n,
            minLength: minLengthI18n(5),
            maxLength: maxLengthI18n(5)
        },
        street: {
            required: requiredI18n,
            minLength: minLengthI18n(3)
        },
        city: {
            required: requiredI18n
        },
        countryId: {
            required: requiredI18n
        },
        countryStateId: {},
        additionalAddressLine1: {}
    }));

    const $v = useVuelidate(rules, formData);
</script>

<template>
    <div
        id="modal-headline"
        aria-label="Edit Address"
        class="address-form"
    >
        <form
            id="account-address"
            ref="formElement"
            name="account-address"
            method="post"
        >
            <div>
                <h3>
                    {{ t('form.addressesTitle') }}
                </h3>

                <div class="registration-container">
                    <div class="registration-column input-wrapper">
                        <label for="salutation">{{ t('form.salutation') }}*</label>
                        <select
                            id="salutation"
                            v-model="formData.salutationId"
                            required
                            name="salutation"
                            aria-label="Customer salutation"
                            autocomplete="on"
                            class="full-width"
                            data-testid="account-address-form-salutation-select"
                            @blur="$v.salutationId.$touch()"
                        >
                            <option
                                disabled
                                selected
                                value=""
                            >
                                {{ t('form.chooseSalutation') }}
                            </option>
                            <option
                                v-for="salutation in getSalutations"
                                :key="salutation.id"
                                :value="salutation.id"
                            >
                                {{ salutation.displayName }}
                            </option>
                        </select>
                        <span
                            v-if="$v.salutationId.$errors[0]"
                            class="select-error"
                        >
                            {{ $v.salutationId.$errors[0].$message }}
                        </span>
                    </div>

                    <!-- Name First -->
                    <div class="registration-column two-column input-wrapper">
                        <label for="first-name">{{ t('form.firstName') }}*</label>
                        <input
                            id="first-name"
                            v-model="formData.firstName"
                            type="text"
                            required
                            name="first-name"
                            :placeholder="t('form.firstNamePlaceholder')"
                            class="full-width"
                            data-testid="account-address-form-firstname-input"
                            @blur="$v.firstName.$touch()"
                        />
                        <span
                            v-if="$v.firstName.$errors[0]"
                            class="select-error"
                        >
                            {{ $v.firstName.$errors[0].$message }}
                        </span>
                    </div>

                    <!-- Name Last -->
                    <div class="registration-column two-column input-wrapper">
                        <label for="last-name">{{ t('form.lastName') }}*</label>
                        <input
                            id="last-name"
                            v-model="formData.lastName"
                            type="text"
                            required
                            name="last-name"
                            :placeholder="t('form.lastNamePlaceholder')"
                            class="full-width"
                            data-testid="account-address-form-lastname-input"
                            @blur="$v.lastName.$touch()"
                        />
                        <span
                            v-if="$v.lastName.$errors[0]"
                            class="select-error"
                        >
                            {{ $v.lastName.$errors[0].$message }}
                        </span>
                    </div>

                    <div class="registration-column input-wrapper">
                        <label for="street-addres">{{ t('form.additionalAddressLine1') }}</label>
                        <input
                            id="additional-address"
                            v-model="formData.additionalAddressLine1"
                            type="text"
                            name="additional-address"
                            placeholder="Adresszusatz"
                            autocomplete="address-line2"
                            class="full-width"
                            data-testid="checkout-pi-additional-address-input"
                            @blur="$v.additionalAddressLine1.$touch()"
                        />
                        <span
                            v-if="$v.additionalAddressLine1.$errors[0]"
                            class="select-error"
                        >
                            {{ $v.additionalAddressLine1.$errors[0].$message }}
                        </span>
                    </div>

                    <div class="registration-column input-wrapper">
                        <label for="street-addres">{{ t('form.streetAddress') }}*</label>
                        <input
                            id="street-address"
                            v-model="formData.street"
                            type="text"
                            required
                            name="street-address"
                            :placeholder="t('form.streetPlaceholder')"
                            autocomplete="street-address"
                            class="full-width"
                            data-testid="account-address-form-street-input"
                            @blur="$v.street.$touch()"
                        />
                        <span
                            v-if="$v.street.$errors[0]"
                            class="select-error"
                        >
                            {{ $v.street.$errors[0].$message }}
                        </span>
                    </div>

                    <div class="registration-column two-column input-wrapper">
                        <label for="postal-code">{{ t('form.postalCode') }}*</label>
                        <input
                            id="postal-code"
                            v-model="formData.zipcode"
                            type="text"
                            required
                            name="postal-code"
                            :placeholder="t('form.postalCodePlaceholder')"
                            autocomplete="postal-code"
                            class="full-width"
                            data-testid="account-address-form-zip-code-input"
                            @blur="$v.zipcode.$touch()"
                        />
                        <span
                            v-if="$v.zipcode.$errors[0]"
                            class="select-error"
                        >
                            {{ $v.zipcode.$errors[0].$message }}
                        </span>
                    </div>

                    <div class="registration-column two-column input-wrapper">
                        <label for="city">{{ t('form.city') }}*</label>
                        <input
                            id="city"
                            v-model="formData.city"
                            type="text"
                            required
                            name="city"
                            :placeholder="t('form.cityPlaceholder')"
                            autocomplete="address-level2"
                            class="full-width"
                            data-testid="account-address-form-city-input"
                            @blur="$v.city.$touch()"
                        />
                        <span
                            v-if="$v.city.$errors[0]"
                            class="select-error"
                        >
                            {{ $v.city.$errors[0].$message }}
                        </span>
                    </div>

                    <SharedCountryStateInput
                        v-model:country-id="formData.countryId"
                        v-model:state-id="formData.countryStateId"
                        :country-id-validation="$v.countryId"
                        class="registration-column"
                    />
                </div>
            </div>

            <div class="submit">
                <PrimaryButton
                    product-button
                    :button-text="t('form.save')"
                    data-testid="account-address-form-submit-button"
                    @click.stop.prevent="invokeSave"
                />
            </div>
        </form>
    </div>
</template>

<style scoped>
    @import '~/assets/scss/basics/address-form.scss';
</style>
